// Analytics API v1 client (see docs/director-dashboard.md).
// Each region is a separate billing installation, so we keep one axios
// instance per region and aggregate on the client.
import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import keycloak from '../keycloak';
import { getRegionApiUrls, REGION_CODES, RegionCode } from '../config';
import {
  AggregateResult,
  AggregatedProducts,
  AggregatedStaffCredits,
  AggregatedSummary,
  AggregatedMonth,
  AnalyticsMeta,
  AnalyticsMonthly,
  AnalyticsPeriodParams,
  AnalyticsPermissions,
  AnalyticsProducts,
  AnalyticsReceivableClient,
  AnalyticsReceivables,
  AnalyticsStaffCredits,
  AnalyticsSummary,
  AnalyticsTopClient,
  AnalyticsTopClients,
  DecimalString,
  WithRegion,
} from './types/analytics';
import {
  fetchAcrossRegions as fetchAcrossRegionsPure,
  mergeMonthly,
  mergeProducts,
  mergeReceivables,
  mergeStaffCredits,
  mergeSummaries,
  mergeTopClients,
  RegionResponses,
} from './analyticsAggregate';

const clients = new Map<string, AxiosInstance>();
type AnalyticsRequestConfig = InternalAxiosRequestConfig & { analyticsRetried?: boolean };
export const ANALYTICS_TIMEOUT_MS = 30000;

const createClient = (baseUrl: string): AxiosInstance => {
  const instance = axios.create({
    baseURL: `${baseUrl}staffapi/analytics/v1/`,
    withCredentials: true,
    timeout: ANALYTICS_TIMEOUT_MS,
  });
  // Read the token on every request: keycloak refreshes it in place.
  instance.interceptors.request.use(async (cfg) => {
    if (keycloak.authenticated) await keycloak.updateToken(30);
    if (keycloak.token) cfg.headers.set('Authorization', `Bearer ${keycloak.token}`);
    // Never send the literal "null": fall back to the app default language (uk).
    cfg.headers.set('Accept-Language', localStorage.getItem('lang') || 'uk');
    return cfg;
  });
  instance.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      if (!axios.isAxiosError(error)) throw error;
      const status = error.response?.status;
      const request = error.config as AnalyticsRequestConfig | undefined;
      if ((status === 401 || status === 498) && request && !request.analyticsRetried && keycloak.authenticated) {
        request.analyticsRetried = true;
        await keycloak.updateToken(-1);
        return instance.request(request);
      }
      return Promise.reject(error);
    },
  );
  return instance;
};

const getRegionUrl = (region: string): string => {
  const url = getRegionApiUrls()[region as RegionCode];
  if (!url) throw new Error(`No backend URL configured for region ${region}`);
  return url.endsWith('/') ? url : `${url}/`;
};

const clientFor = (region: string): AxiosInstance => {
  const url = getRegionUrl(region);
  let c = clients.get(url);
  if (!c) {
    c = createClient(url);
    clients.set(url, c);
  }
  return c;
};

const toQuery = (params: AnalyticsPeriodParams & { limit?: number } = {}) => {
  const q: Record<string, string | number> = {};
  if (params.date_from && params.date_to) {
    q.date_from = params.date_from;
    q.date_to = params.date_to;
  } else if (params.months) {
    q.months = params.months;
  }
  if (params.limit) q.limit = params.limit;
  if (params.refresh) q.refresh = 1;
  return q;
};

type Params = AnalyticsPeriodParams & { limit?: number; signal?: AbortSignal };

/** Region of the current admin session (same key the rest of the app uses). */
export const currentRegion = (): RegionCode => {
  const r = localStorage.getItem('region_ocp_zone') as RegionCode | null;
  return r && REGION_CODES.includes(r) ? r : 'ua-1';
};

const analyticsService = {
  /** Standard UserPermissionsMixin endpoint: {"analytics.summary": true, ...}. */
  getPermissions: (region: string) => clientFor(region).get<AnalyticsPermissions>('permissions'),
  getSummary: (region: string, params: Params = {}) =>
    clientFor(region).get<AnalyticsSummary>('summary', { params: toQuery({ refresh: params.refresh }), signal: params.signal }),
  getMonthly: (region: string, params: Params = {}) =>
    clientFor(region).get<AnalyticsMonthly>('monthly', { params: toQuery(params), signal: params.signal }),
  getProducts: (region: string, params: Params = {}) =>
    clientFor(region).get<AnalyticsProducts>('products', { params: toQuery(params), signal: params.signal }),
  getTopClients: (region: string, params: Params = {}) =>
    clientFor(region).get<AnalyticsTopClients>('top-clients', { params: toQuery(params), signal: params.signal }),
  getReceivables: (region: string, params: Params = {}) =>
    clientFor(region).get<AnalyticsReceivables>('receivables', {
      params: toQuery({ limit: params.limit, refresh: params.refresh }),
      signal: params.signal,
    }),
  getStaffCredits: (region: string, params: Params = {}) =>
    clientFor(region).get<AnalyticsStaffCredits>('staff-credits', { params: toQuery(params), signal: params.signal }),
};

/** Fetch one endpoint from all selected regions (allSettled) and merge; see analyticsAggregate. */
export const fetchAcrossRegions = <T extends { meta: AnalyticsMeta }, R>(
  regions: string[],
  fetcher: (region: string) => Promise<{ data: T }>,
  merge: (responses: RegionResponses<T>) => R,
): Promise<AggregateResult<R>> => fetchAcrossRegionsPure(regions, fetcher, merge, getRegionApiUrls());

export const analyticsAggregator = {
  allRegions: (): string[] => [...REGION_CODES],
  summary: (regions: string[], params: Params = {}): Promise<AggregateResult<AggregatedSummary | null>> =>
    fetchAcrossRegions(regions, (r) => analyticsService.getSummary(r, params), mergeSummaries),
  monthly: (regions: string[], params: Params = {}): Promise<AggregateResult<AggregatedMonth[]>> =>
    fetchAcrossRegions(regions, (r) => analyticsService.getMonthly(r, params), mergeMonthly),
  products: (regions: string[], params: Params = {}): Promise<AggregateResult<AggregatedProducts>> =>
    fetchAcrossRegions(regions, (r) => analyticsService.getProducts(r, params), mergeProducts),
  topClients: (
    regions: string[],
    params: Params = {},
  ): Promise<AggregateResult<Array<WithRegion<AnalyticsTopClient>>>> =>
    fetchAcrossRegions(
      regions,
      (r) => analyticsService.getTopClients(r, params),
      (res) => mergeTopClients(res, params.limit),
    ),
  receivables: (
    regions: string[],
    params: Params = {},
  ): Promise<
    AggregateResult<{ total_usd: DecimalString | null; clients: Array<WithRegion<AnalyticsReceivableClient>> }>
  > =>
    fetchAcrossRegions(
      regions,
      (r) => analyticsService.getReceivables(r, params),
      (res) => mergeReceivables(res, params.limit),
    ),
  staffCredits: (regions: string[], params: Params = {}): Promise<AggregateResult<AggregatedStaffCredits>> =>
    fetchAcrossRegions(
      regions,
      (r) => analyticsService.getStaffCredits(r, params),
      (res) => mergeStaffCredits(res, params.limit),
    ),
};

export default analyticsService;
